// Pure gate helpers extracted from `coach-exercise-sync.service.ts` so they can
// be unit-tested without pulling Angular DI / PouchDB into the test runtime.
// Production code in the service imports from here — tests pin the same shipped
// implementation, not a re-write.

import { describeErrorForLog } from '@cf-shared/error-describe';
import { CustomExercise } from '@cf-shared/data-model/exercise';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { isOfflineOrTransientNetworkError, isIdbConnectionError, isClosedDatabaseHandleError } from './error-patterns';
import { CloudFunctionNetworkError, wasReportedByCloudFunctions } from './cloud-functions-errors';
import { isTimeoutError } from './timeout-signal';
import { stampCoachChannelRevision } from './exercise-fork-update';
import { WriteOutcome } from './database-wrapper-save-recovery';

// Rehydrate per-user fields that `stripFieldsForPublish` (server/functions/src/publish-exercise.ts)
// removes before writing to `global_exercises`. Called from every apply-pipeline write site to
// keep the invariant in one place — if `stripFieldsForPublish` grows new fields, this is the
// only spot the receiver needs to update.
//   - target: the doc about to be saved into `userdb-*` (clone on first-arrival, overlay on overwrite)
//   - source: the local canonical doc to copy from on overwrite paths; null for first-arrival
function rehydrateStrippedFields(target: CustomExercise, source: CustomExercise | null): void {
  // `usedInSlots`: required array on the type; default [] for first-arrival so the picker's
  // `.usedInSlots.includes(...)` never sees undefined.
  target.usedInSlots = source?.usedInSlots ?? [];
  // `virtuallyDeleted`: optional flag; preserve only when truthy on canonical so a coach
  // republish doesn't silently undo the client's local virtual-delete.
  if (source?.virtuallyDeleted) target.virtuallyDeleted = true;
}

/**
 * Builds the document the coach-exercise apply pipeline should save into the user database
 * for one coach-channel staging document, or returns null when no save is needed.
 *
 * - No existing copy (first arrival): a clone of the staging doc without its `_rev`, so the
 *   user database assigns its own.
 * - Existing copy: an overlay of the staging doc that keeps the copy's `_rev` and its per-user
 *   fields. The caller copies it onto the existing object with `deepOverwriteCouchSafe`, so the
 *   object other DocWatchers hold stays the same object.
 *
 * Both carry `local.coachChannelRevision` = the staging `_rev` (see stampCoachChannelRevision).
 *
 * `existing` is the identity-mapped object other DocWatchers share, not a fresh read of the
 * stored body. When an earlier save of it did not land, it can already hold coach content the
 * database never received, at an unchanged `_rev` (the wrapper leaves an unsaved edit on the
 * object for the next save to carry; design-docs/pass-through-db.md, "WriteOutcome"). The
 * caller then passes `lastSaveLanded = false`, and the copy is written even when it compares
 * equal.
 */
export function planCoachDocWrite(
  stagingDoc: CustomExercise,
  existing: CustomExercise | null,
  lastSaveLanded = true,
): CustomExercise | null {
  if (!existing) {
    const clone = deepClone(stagingDoc);
    delete clone._rev;
    rehydrateStrippedFields(clone, null);
    stampCoachChannelRevision(clone, stagingDoc._rev);
    return clone;
  }
  const overlay = deepClone(stagingDoc);
  overlay._rev = existing._rev;
  rehydrateStrippedFields(overlay, existing);
  stampCoachChannelRevision(overlay, stagingDoc._rev);
  // if the copy already matches, skip the save (#6367): the pipeline replays every staging doc
  // at every app start, and each save adds a revision even when nothing changed. The comparison
  // ignores `_` and `db__` keys, so the write stamp does not count, but the staging revision
  // does, through `local.coachChannelRevision`.
  if (lastSaveLanded && deepCompareEQ(existing, overlay)) return null;
  return overlay;
}

/**
 * Runs one save of a coach copy and keeps `idsWithUnlandedSave` true to what reached the
 * database (#6367). The id is marked BEFORE the save is awaited, so a save that throws leaves it
 * marked, and it is cleared only when the wrapper answers `Written`; `DroppedIntoRecovery` keeps
 * it marked too. While an id is marked, the caller passes `lastSaveLanded = false` to
 * planCoachDocWrite, so the next apply writes instead of trusting the shared in-memory object.
 */
export async function saveCoachCopyAsync(
  id: string,
  idsWithUnlandedSave: Set<string>,
  save: () => Promise<WriteOutcome>,
): Promise<void> {
  idsWithUnlandedSave.add(id);
  const outcome = await save();
  if (outcome === WriteOutcome.Written) idsWithUnlandedSave.delete(id);
}

/**
 * Returns true when Replication A (`global_exercises` coach channel → local
 * staging) should be running for the given `coachUid` value read from
 * `coach-relationship/as-client`. Gate is on truthiness — Phase 3 of #3198
 * relies on this so the mutate-event detach path (clears `coachUid` instead
 * of deleting the doc) correctly tears down the replication.
 */
export function shouldRunReplicationAForCoach(nextCoachUid: string | undefined): boolean {
  return !!nextCoachUid;
}

/**
 * Given the previously-known `coachUid` and the next value read from the doc
 * on a DocWatcher change, returns true when the reconciler should restart
 * Replication A. Used to skip redundant reconciles when a DocChange fired
 * for an unrelated field on the same doc.
 */
export function coachReplicationNeedsRestart(prev: string | undefined, next: string | undefined): boolean {
  return prev !== next;
}

/** What `reportCoachSyncFailure` did with a failure. */
export enum CoachSyncFailureOutcome {
  /** the network was unavailable or the request was aborted: logged on the device only */
  Transient = 'transient',
  /** the device lost its IndexedDB connection: logged on the device only */
  DeviceStorageLost = 'device-storage-lost',
  /** the cloud-functions layer already reported the failure, or skipped it offline: logged on the device only */
  HandledUpstream = 'handled-upstream',
  /** any other failure: reported to Sentry */
  Reported = 'reported',
}

/**
 * Reports a failure of a coach-sync step that retries on its own: the coach's own-exercise
 * sweep (df-c357a686cb46) and the client's read of its copy before applying a coach
 * exercise (df-ae300b22d281).
 *
 * Both run at app start, and their reads can pass through to the remote database during
 * initial sync, so an offline app or a request the platform aborts ends up here routinely.
 * The step runs again later (`retriedBy` says when, for the console line), so a network drop
 * is expected, not a fault: it is written to the console and NOT sent to Sentry, where it
 * used to bury the failures that have a real cause. A lost IndexedDB connection is treated the
 * same way (df-860a55b61154): it is a device condition the app drops from Sentry everywhere
 * else, but the beforeSend hook cannot see it here, because it sits in the problem's context
 * and not in the event message. A cloud-function failure that layer already reported, or chose
 * not to report because the device was offline, is only logged too (df-b3c0d25c0dcf): an iOS
 * native timeout `callApiAsync` marked, or a 502/503 `callable()` reported after its retries.
 * A wrapped timeout is the exception and is still reported (see isTimeoutFailure). Every other failure is still reported
 * under the caller's condition name (so existing Sentry issues keep grouping), described
 * with describeErrorForLog so a PouchDB `{status, error, reason}` error stays readable.
 */
export function reportCoachSyncFailure(
  trackSilentProblem: (conditionName: string, context: Record<string, unknown>) => void,
  conditionName: string,
  err: unknown,
  context: Record<string, unknown>,
  retriedBy: string,
): CoachSyncFailureOutcome {
  const described = describeErrorForLog(err);
  // if the network was unavailable, log it here and leave the retry to the step's next run
  if (isOfflineOrTransientNetworkError(err) && !isTimeoutFailure(err)) {
    console.warn(`${conditionName} (network unavailable; ${retriedBy})`, { ...context, err: described });
    return CoachSyncFailureOutcome.Transient;
  }
  // if the device lost its IndexedDB connection, log it here and leave the retry to the next run
  if (isLostIdbConnectionFailure(err)) {
    console.warn(`${conditionName} (device storage connection lost; ${retriedBy})`, { ...context, err: described });
    return CoachSyncFailureOutcome.DeviceStorageLost;
  }
  // if the cloud-functions layer already reported it (or skipped it offline), log it only.
  // a wrapped timeout stays reported here on purpose, because this report names the step and the doc
  if (wasReportedByCloudFunctions(err) && !isTimeoutFailure(err)) {
    console.warn(`${conditionName} (already handled by the cloud-functions layer; ${retriedBy})`, { ...context, err: described });
    return CoachSyncFailureOutcome.HandledUpstream;
  }
  console.error(conditionName, { ...context, err: described });
  trackSilentProblem(conditionName, { ...context, err: described });
  return CoachSyncFailureOutcome.Reported;
}

/**
 * True when a failure is a request that ran out of time, either as the raw TimeoutError or
 * wrapped by the cloud-function layer. That layer retries a timeout, then throws it wrapped in
 * CloudFunctionNetworkError, which the offline classifier matches by name. Since #6058 that layer
 * also files its own `Cloud function "<name>" timed out` report, so a timed-out sweep publish
 * reaches Sentry twice: that report, and this one, which is kept because it names the sweep and
 * the document. A timeout is a real problem (an unresponsive server), not a device being offline.
 */
function isTimeoutFailure(err: unknown): boolean {
  if (isTimeoutError(err)) return true;
  return err instanceof CloudFunctionNetworkError && isTimeoutError(err.originalError);
}

/**
 * True when a failure is WebKit's lost IndexedDB connection ("Connection to Indexed Database
 * server lost", and the other device-fault messages `isIdbConnectionError` lists), raw or
 * wrapped by the cloud-function layer. Matched on the message on purpose, so PouchDB's
 * synthetic `indexed_db_went_bad` (a store that went bad) is still reported. The closed-handle
 * messages are excluded too: they mean our own code called a database it had closed, which is
 * a fault in the app, not in the device.
 */
function isLostIdbConnectionFailure(err: unknown): boolean {
  const failure = err instanceof CloudFunctionNetworkError ? err.originalError : err;
  const message = (failure as { message?: unknown } | null | undefined)?.message;
  if (typeof message !== 'string') return false;
  return isIdbConnectionError(message) && !isClosedDatabaseHandleError(failure);
}
